import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  PageContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui";
import { Breadcrumb } from "@repo/ui";
import { useQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { useParams } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { arianeOrganisation, racineOrganisation } from "../../lib/ariane";
import { dateCourteHeure } from "@repo/utils";

type Acteur =
  | { kind: "humain"; nom?: string; email?: string }
  | { kind: "ia"; model: string }
  | { kind: "system" }
  | { kind: "referent" };

function acteurBadge(acteur: Acteur) {
  switch (acteur.kind) {
    case "humain":
      return <Badge variant="default">Humain</Badge>;
    case "ia":
      return <Badge variant="secondary">IA</Badge>;
    case "system":
      return <Badge variant="outline">Système</Badge>;
    case "referent":
      return <Badge variant="purple">Référent</Badge>;
  }
}

export function ParcoursEvaluateurDetailAdmin() {
  const { id } = useParams<{ id: string }>();

  const data = useQuery(
    api.superAdmin.obtenirDetailParcoursEvaluateurSuperAdmin,
    id ? { id: id as Id<"parcoursEvaluateur"> } : "skip",
  );

  if (data === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const { evaluateur, notations } = data;

  const nom =
    evaluateur.acteur.kind === "ia"
      ? `IA (${evaluateur.acteur.model})`
      : evaluateur.acteur.kind === "system"
        ? "Système"
        : evaluateur.anonyme
          ? "Anonyme"
          : [evaluateur.prenomEvaluateur, evaluateur.nomEvaluateur]
              .filter(Boolean)
              .join(" ") || "—";

  return (
    <PageContainer>
      <Breadcrumb
        className="mb-6"
        items={[
          ...arianeOrganisation(evaluateur.organisationId, evaluateur.organisationNom, "campagnes"),
          // Un évaluateur hors campagne remonte à la vue « hors campagne »,
          // sur son onglet Évaluateurs.
          evaluateur.campagneId
            ? {
                label: evaluateur.campagneNom ?? "Campagne",
                href: `/campagnes/${evaluateur.campagneId}/evaluateurs`,
              }
            : {
                label: "Hors campagne",
                href: `${racineOrganisation(evaluateur.organisationId)}/hors-campagne?onglet=evaluateurs`,
              },
          { label: nom },
        ]}
      />

      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between gap-4">
            <CardTitle className="text-2xl">{nom}</CardTitle>
            {acteurBadge(evaluateur.acteur)}
          </div>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-muted-foreground">Email</dt>
              <dd>{evaluateur.emailEvaluateur ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Organisation</dt>
              <dd>{evaluateur.organisationNom ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Campagne</dt>
              <dd>{evaluateur.campagneNom ?? "—"}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Lien</dt>
              <dd>
                {evaluateur.lienEnvoye ? (
                  <Badge variant="success">Envoyé</Badge>
                ) : (
                  <Badge variant="outline">Non envoyé</Badge>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Créé le</dt>
              <dd>{dateCourteHeure(evaluateur.dateCreation)}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notations ({notations.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>Évaluation</TableHead>
                <TableHead>Évaluateur</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Job ID</TableHead>
                <TableHead>Modifié le</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {notations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    <p className="text-muted-foreground">Aucune notation</p>
                  </TableCell>
                </TableRow>
              ) : (
                notations.map((n) => (
                  <TableRow key={n._id}>
                    <TableCell className="text-sm">
                      {n.evaluationLibelle}
                    </TableCell>
                    <TableCell className="text-sm">{n.nomEvaluateur}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{n.statut}</Badge>
                    </TableCell>
                    <TableCell
                      className="text-xs text-muted-foreground font-mono truncate"
                      title={n.jobId ?? undefined}
                    >
                      {n.jobId ?? "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {dateCourteHeure(n.dateModification)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
